import { Alert, Button, Group, Loader, Stack, Text } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconAlertCircle } from '@tabler/icons-react';
import { DocumentReference, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { Customer, KfhtradeUpdateKycDatesRequest, KfhtradeUpdateKycDatesResult, Service } from '../types';
import { useFunctions } from '../helpers/firebaseContext';
import { fromMantineDate, MantineDateValue, toMantineDate } from '../helpers/mantineDates';
import { getCustomerFromPaciPersonalData } from '../helpers/common';

interface UpdateKfhtradeKycDatesModalContentProps {
    service: Service;
    customerRef: DocumentReference;
    onClose: () => void;
}

/**
 * Admin-only manual push of the KYC and civil ID expiry dates to the KFHTrade
 * OMS (kfhtradeUpdateKycDates callable).  Pre-fills from the customer's
 * lastValidKYC (civil ID expiry falls back to the PACI card expiry) so the
 * usual case is just "re-push what the CRM has"; either date can be edited
 * before sending.
 */
function UpdateKfhtradeKycDatesModalContent({ service, customerRef, onClose }: UpdateKfhtradeKycDatesModalContentProps) {
    const functions = useFunctions();
    functions.region = "europe-west1";
    const updateKycDates = httpsCallable<KfhtradeUpdateKycDatesRequest, KfhtradeUpdateKycDatesResult>(functions, "kfhtradeUpdateKycDates");

    const [loadingCustomer, setLoadingCustomer] = useState(true);
    const [kfhtradeId, setKfhtradeId] = useState<string | undefined>();
    const [kycExpiryDate, setKycExpiryDate] = useState<MantineDateValue>(null);
    const [civilIdExpiryDate, setCivilIdExpiryDate] = useState<MantineDateValue>(null);
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string>("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const customer = (await getDoc(customerRef)).data() as Customer | undefined;
                if (cancelled) return;
                setKfhtradeId(customer?.kfhtrade_id);
                const paci = getCustomerFromPaciPersonalData(service.paciData ?? customer?.paciData);
                setKycExpiryDate(toMantineDate(customer?.lastValidKYC?.kycExpiryDate?.toDate()));
                setCivilIdExpiryDate(toMantineDate(
                    customer?.lastValidKYC?.civilIDExpiryDate?.toDate()
                    ?? paci?.civilIDExpiryDate?.toDate(),
                ));
            } catch (error: any) {
                console.error("Failed to load customer for KFHTrade KYC dates update", error);
                if (!cancelled) setErrorMsg(error?.message ?? "Failed to load the customer");
            } finally {
                if (!cancelled) setLoadingCustomer(false);
            }
        })();
        return () => { cancelled = true; };
    }, [customerRef, service.paciData]);

    const submit = async () => {
        const kyc = fromMantineDate(kycExpiryDate);
        const civil = fromMantineDate(civilIdExpiryDate);
        if (!kyc || !civil) {
            setErrorMsg("Both dates are required");
            return;
        }
        setErrorMsg("");
        setSubmitting(true);
        try {
            const result = await updateKycDates({
                customerID: customerRef.id,
                serviceID: service.id!,
                kycExpiryDate: toMantineDate(kyc)!,
                civilIdExpiryDate: toMantineDate(civil)!,
            });
            const { outcome, message, customerNo } = result.data;
            notifications.show({
                title: outcome === 'failed' ? 'KFHTrade KYC dates update failed' : 'KFHTrade KYC dates updated',
                message: customerNo ? `${message} (Customer No: ${customerNo})` : message,
                color: outcome === 'failed' ? 'red' : 'green',
            });
            if (outcome !== 'failed') {
                onClose();
            } else {
                setErrorMsg(message);
            }
        } catch (error: any) {
            console.error("kfhtradeUpdateKycDates failed", error);
            setErrorMsg(error?.message ?? 'Unknown error');
        } finally {
            setSubmitting(false);
        }
    };

    if (loadingCustomer) {
        return <Group justify="center" p="md"><Loader size="sm" /></Group>;
    }

    return (
        <Stack gap="sm">
            <Text size="sm">
                Pushes these expiry dates to the KFHTrade OMS for customer No. <b>{kfhtradeId ?? "—"}</b> via the kycupdate API.
                Pre-filled from the CRM&apos;s last valid KYC; edit them if the OMS record needs a different value.
            </Text>

            {!kfhtradeId && (
                <Alert color="red" icon={<IconAlertCircle size={16} />}>
                    This customer has no KFHTrade ID yet. Create the OMS account first.
                </Alert>
            )}

            <DateInput
                label="KYC expiry date"
                value={kycExpiryDate}
                onChange={(value) => setKycExpiryDate(toMantineDate(value))}
                valueFormat="DD/MM/YYYY"
                disabled={submitting}
                withAsterisk
                clearable
            />
            <DateInput
                label="Civil ID expiry date"
                value={civilIdExpiryDate}
                onChange={(value) => setCivilIdExpiryDate(toMantineDate(value))}
                valueFormat="DD/MM/YYYY"
                disabled={submitting}
                withAsterisk
                clearable
            />

            {errorMsg && (
                <Alert color="red" icon={<IconAlertCircle size={16} />}>{errorMsg}</Alert>
            )}

            <Group justify="flex-end" mt="xs">
                <Button variant="default" onClick={onClose} disabled={submitting}>Cancel</Button>
                <Button
                    color="cyan"
                    loading={submitting}
                    disabled={!kfhtradeId || !kycExpiryDate || !civilIdExpiryDate}
                    onClick={submit}
                >
                    Update on OMS
                </Button>
            </Group>
        </Stack>
    );
}

export default UpdateKfhtradeKycDatesModalContent;
